import { useQueryClient } from '@tanstack/react-query'
import { AxiosHeaders } from 'axios'
import { useContext } from 'react'
import router from 'next/router'

import { AuthorizationContext } from 'src/shared/context'

import { apiPost } from '../axios.config'
import { API_ROUTES } from '../routes'
import { useMutationHook } from './mutationHook'

interface IBaseUpdateUser {
  firstName: string
  lastName: string
  middleName?: string
}

interface IAdminUserUpdate extends IBaseUpdateUser {
  id: string
}

async function fetchUpdateUser(path: string, body: IBaseUpdateUser | IAdminUserUpdate, token?: string | null) {
  const headers = new AxiosHeaders({
    Authorization: `Bearer ${token}`,
  })

  try {
    const { data } = await apiPost<IBaseUpdateUser | IAdminUserUpdate, void>({ path, body, ...(token && { headers }) })

    return data
  } catch (error: any) {
    if (error.response?.status === 401) {
      throw new Error('Unauthorized')
    }
    throw error
  }
}

export const useUpdateAuthor = (options?: { onSuccess?: () => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient()
  const { token, setToken } = useContext(AuthorizationContext)
  const path = API_ROUTES.USER_UPDATE

  const mutationFn = (url: string, body: IBaseUpdateUser) => fetchUpdateUser(url, body, token)

  return useMutationHook({
    path,
    mutationKey: [path],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.AUTHORS],
      })
      options?.onSuccess?.()
    },
    onError: (error: Error) => {
      if (error.message === 'Unauthorized') {
        setToken('')
        router.push('/')
      }
    },
  })
}

export const useAdminUpdateAuthor = (options?: { onSuccess?: () => void; onError?: (error: Error) => void }) => {
  const queryClient = useQueryClient()
  const path = API_ROUTES.ADMIN_USER_UPDATE
  const mutationFn = (url: string, body: IAdminUserUpdate) => fetchUpdateUser(url, body)

  return useMutationHook({
    path,
    mutationKey: [path],
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [API_ROUTES.AUTHORS],
      })
      options?.onSuccess?.()
    },
    onError: options?.onError,
  })
}
